import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuChevronsLeft, LuX } from "react-icons/lu";
import { NAV_ITEMS } from "../../utils/Constant";
import NavItem from "../../common/NavItem";
import { useSidebar } from "../../hooks/SidebarContext";
import { useSelector } from "react-redux";

export default function Sidebar() {
  const { isMobileOpen, setIsMobileOpen, isCollapsed, toggleCollapsed } = useSidebar();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = !!user?.admin;
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const SidebarContent = ({ collapsed, onNavClick }) => (
    <div className="flex h-full flex-col">
      <div
        className={`flex h-16 shrink-0 items-center border-b border-white/[0.06] px-5 ${
          collapsed ? "lg:justify-center lg:px-0" : ""
        }`}
      >
        {!collapsed && (
          <div className="relative flex min-w-0 flex-1 items-center">
            <motion.div
              animate={{
                opacity: [0.25, 0.5, 0.25],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute inset-0 -z-10 rounded-full bg-[#5B7CFF]/30 blur-xl"
            />

            <img
              src="https://mediafiles.projects.oceanicstudio.net/inads-cms-staging/ksas/logo_image/1709961414_logo-wht.png"
              alt="KSAS"
              className="h-auto w-full max-w-[210px] object-contain object-left"
            />
          </div>
        )}

        {collapsed && (
          <img
            src="https://www.image2url.com/r2/default/images/1786529594192-c86350ef-49d0-4876-bbf4-479919017a3a.png"
            alt="KSAS"
            className="h-7 w-7 shrink-0 rounded-full object-contain"
          />
        )}

        <button
          type="button"
          onClick={() => setIsMobileOpen(false)}
          className="ml-auto rounded-md p-1.5 text-slate-500 hover:bg-white/[0.06] hover:text-white lg:hidden"
        >
          <LuX size={16} />
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {visibleNavItems.map((item) => (
          <NavItem
            key={item.to}
            {...item}
            collapsed={collapsed}
            onClick={onNavClick}
          />
        ))}
      </nav>

      <div className="hidden border-t border-white/[0.06] p-3 lg:block">
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[12px] font-medium text-slate-500 transition-colors duration-200 hover:bg-white/[0.04] hover:text-slate-300 ${
            collapsed ? "lg:justify-center" : ""
          }`}
        >
          <LuChevronsLeft
            size={15}
            className={`shrink-0 transition-transform duration-300 ${collapsed ? "rotate-180" : ""}`}
          />
          <span
            className={`overflow-hidden whitespace-nowrap ${collapsed ? "lg:hidden" : ""}`}
          >
            Collapse
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden transition-all duration-300 ease-in-out lg:block ${
          isCollapsed ? "lg:w-[76px]" : "lg:w-64"
        }`}
        style={{
          background:
            "linear-gradient(160deg, #121A3D 0%, #0B1128 60%, #080B1C 100%)",
          borderRight: "1px solid transparent",
          borderImage:
            "linear-gradient(180deg, rgba(91,124,255,0.35), rgba(91,124,255,0.03)) 1",
          boxShadow: "4px 0 24px -8px rgba(0,0,0,0.5)",
        }}
      >
        <div
          className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full opacity-30 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(91,124,255,0.4), transparent 70%)",
          }}
        />
        <SidebarContent collapsed={isCollapsed} />
      </aside>
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{
                type: "tween",
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[80vw] overflow-hidden shadow-2xl lg:hidden"
              style={{
                background:
                  "linear-gradient(160deg, #121A3D 0%, #0B1128 60%, #080B1C 100%)",
              }}
            >
              <div
                className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full opacity-30 blur-3xl"
                style={{
                  background:
                    "radial-gradient(circle, rgba(91,124,255,0.4), transparent 70%)",
                }}
              />
              <SidebarContent
                collapsed={false}
                onNavClick={() => setIsMobileOpen(false)}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
